import { Link, router } from "@inertiajs/react"
import { sanitizeContentHtml } from "@/lib/sanitize-html"
import { buildVideoEmbedUrl } from "@/lib/video-embed"
import { useState } from "react"
import { ArrowRight, Check, Download, ExternalLink, FileText, Loader2, Play, Save, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { QuizSection } from "./QuizSection"

// The body of a member lesson: header, content, media, questions or quiz,
// resources, and Mark complete. Shared by the lesson page and the track
// player (plan 2026-09-23-002). `trackId` keeps every post inside the player;
// `nextStep` is the player's next lesson, offered once this one is done.

function VideoEmbed({ videoUrl }) {
  const embedUrl = buildVideoEmbedUrl(videoUrl)
  if (!embedUrl) return null

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-zinc-900">
      <iframe
        src={embedUrl}
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        title="Lesson video"
      />
    </div>
  )
}

function QuestionsSection({ questions, courseId, lessonId, trackId }) {
  const [responses, setResponses] = useState(() => {
    const initial = {}
    questions.forEach((q) => {
      initial[q.id] = q.responseText || ""
    })
    return initial
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }))
    setSaved(false)
  }

  const handleSave = () => {
    setSaving(true)
    const responsesArray = Object.entries(responses)
      .filter(([_, text]) => text.trim().length > 0)
      .map(([questionId, responseText]) => ({
        question_id: questionId,
        response_text: responseText,
      }))

    router.post(
      `/app/member/courses/${courseId}/lessons/${lessonId}/question_responses`,
      { responses: responsesArray, track: trackId },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSaved(true)
          setSaving(false)
        },
        onError: () => {
          setSaving(false)
        },
      }
    )
  }

  if (!questions || questions.length === 0) return null

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-zinc-900">Questions</h2>
      <div className="space-y-6">
        {questions.map((question, index) => (
          <div key={question.id} className="space-y-2">
            <Label className="text-sm font-medium text-zinc-900">
              {index + 1}. {question.text}
            </Label>
            <Textarea
              value={responses[question.id] || ""}
              onChange={(e) => handleChange(question.id, e.target.value)}
              placeholder="Type your answer..."
              className="min-h-24"
            />
          </div>
        ))}
      </div>
      {/* Honesty, not a behaviour change: a coach has always been able to read
          these. Saying so is cheaper than a privacy toggle, which would carry
          schema cost the answers do not justify. */}
      <p className="text-xs text-zinc-500">
        Your coach can read these answers.
      </p>
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full sm:w-auto min-h-[44px]"
        variant="gradient"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : saved ? (
          <>
            <Check className="h-4 w-4" />
            Saved
          </>
        ) : (
          <>
            <Save className="h-4 w-4" />
            Save Responses
          </>
        )}
      </Button>
    </div>
  )
}

function ResourcesList({ resources }) {
  if (!resources || resources.length === 0) return null

  const getIcon = (type) => {
    switch (type) {
      case "video":
        return Play
      case "link":
        return ExternalLink
      default:
        return FileText
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-zinc-900">Resources</h2>
      <div className="space-y-2">
        {resources.map((resource) => {
          const Icon = getIcon(resource.resourceType)
          const href = resource.resourceType === "file" ? resource.fileUrl : resource.url
          if (!href) return null

          return (
            <a
              key={resource.id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-200 bg-white hover:border-kf-red/30 hover:shadow-sm transition-all min-h-[44px]"
            >
              <Icon className="h-5 w-5 text-zinc-500 shrink-0" />
              <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
                {resource.title}
              </span>
              {resource.resourceType === "file" ? (
                <Download className="h-4 w-4 text-zinc-400 shrink-0" />
              ) : (
                <ExternalLink className="h-4 w-4 text-zinc-400 shrink-0" />
              )}
            </a>
          )
        })}
      </div>
    </div>
  )
}

export function LessonBody({ course, lesson, questions = [], resources = [], completed, quiz = null, trackId = null, nextStep = null }) {
  const [completing, setCompleting] = useState(false)

  const handleComplete = () => {
    setCompleting(true)
    router.post(
      `/app/member/courses/${course.id}/lessons/${lesson.id}/complete`,
      trackId ? { track: trackId } : {},
      { onFinish: () => setCompleting(false) }
    )
  }

  return (
    <>
        {/* Lesson header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-zinc-900">{lesson.title}</h1>
          {lesson.coachOnly && (
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" />
              Coach Only
            </Badge>
          )}
          {completed && (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200 gap-1">
              <Check className="h-3 w-3" />
              Completed
            </Badge>
          )}
        </div>
      </div>

      {/* Lesson content */}
      {lesson.content && (
        <div
          className="prose prose-zinc max-w-none prose-headings:font-display prose-a:text-kf-red prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(lesson.content || "") }}
        />
      )}

      {/* Media: PDF or video */}
      {lesson.mediaKind === "pdf" ? (
        lesson.mediaFileUrl ? (
          <div className="rounded-xl overflow-hidden border border-zinc-200 bg-white">
            <iframe
              src={lesson.mediaFileUrl}
              className="w-full h-[80vh]"
              title="Lesson PDF"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-zinc-400" />
            <p className="text-sm text-zinc-500">
              No PDF has been uploaded for this lesson yet.
            </p>
          </div>
        )
      ) : (
        <VideoEmbed videoUrl={lesson.videoUrl} />
      )}

      {/* Questions — a quiz lesson grades them and completes itself on a pass */}
      {quiz ? (
        <QuizSection
          key={quiz.lastAttempt?.attemptNumber ?? 0}
          questions={questions}
          quiz={quiz}
          courseId={course.id}
          lessonId={lesson.id}
          trackId={trackId}
          nextStep={nextStep}
        />
      ) : (
        <QuestionsSection
          questions={questions}
          courseId={course.id}
          lessonId={lesson.id}
          trackId={trackId}
        />
      )}

      {/* Resources */}
      <ResourcesList resources={resources} />

      {/* Mark Complete */}
      {!completed && !quiz && (
        <div className="pt-4 border-t border-zinc-200">
          <Button
            onClick={handleComplete}
            disabled={completing}
            variant="gradient"
            className="w-full sm:w-auto min-h-[44px]"
            size="lg"
          >
            {completing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Marking Complete...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Mark Lesson Complete
              </>
            )}
          </Button>
        </div>
      )}

      {/* In the track player, a finished lesson offers the next step. */}
      {completed && !quiz && nextStep && !nextStep.locked && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <span className="text-sm text-green-800">
            <Check className="inline h-4 w-4 mr-1" />
            Done. Next: {nextStep.title}
          </span>
          <Button asChild variant="gradient" className="min-h-[44px]">
            <Link href={nextStep.href}>
              Next step
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </>
  )
}
